import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  commitSeityBond,
  type CommitSeityBondResponse,
} from '@/api/seityBond';

// SEITY-BOND-COMMITMENT-V0 (2026-05-15) — responsibility bond commitment.
//
// The human accepts responsibility for their agent's actions. A SeityBond
// DB row is written. No cryptographic credential is generated in V0 — that's
// V1, gated on Pavel's Elixir substrate.
//
// The point of this page is not legal-grade attestation. It's a moment of
// directed intention: the user names their seity, writes a short
// responsibility statement, and clicks the commitment button. The DB row
// makes the audit chain real: agent_run -> AgentIdentity -> SeityBond ->
// user_id.

const DEFAULT_VOW = [
  'I accept responsibility for the actions of my agent.',
  '',
  'If my agent acts on my behalf, I will treat its choices as my own —',
  'the work it ships, the messages it sends, the decisions it makes.',
  '',
  'I will keep its kill switches active. I will not run it unsupervised',
  'in contexts where I am unwilling to answer for what it does.',
].join('\n');

const PAGE_WRAPPER: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '48px 16px',
};

const CARD: React.CSSProperties = {
  background: 'var(--sp-bg-elev)',
  border: '1px solid var(--sp-border)',
  borderRadius: 8,
  padding: 32,
  width: '100%',
  maxWidth: 640,
};

const TITLE: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  color: 'var(--sp-text)',
  marginBottom: 16,
  marginTop: 0,
};

const INTRO: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--sp-text-muted)',
  marginBottom: 12,
  marginTop: 0,
};

const FOOTNOTE: React.CSSProperties = {
  ...INTRO,
  fontSize: 12,
  color: 'var(--sp-text-dim)',
  marginBottom: 24,
};

const FIELD_GAP: React.CSSProperties = { height: 16 };

const TEXTAREA_STYLE: React.CSSProperties = {
  fontFamily: 'var(--sp-font-mono)',
  fontSize: 12.5,
  lineHeight: 1.55,
  resize: 'vertical',
  minHeight: 200,
};

export function SeityBondCommitPage() {
  const navigate = useNavigate();
  const [seityName, setSeityName] = useState('');
  const [vowsText, setVowsText] = useState(DEFAULT_VOW);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CommitSeityBondResponse | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    const trimmedName = seityName.trim();
    const trimmedVow = vowsText.trim();
    if (!trimmedName) {
      setError('Give your seity a name before committing.');
      return;
    }
    if (!trimmedVow) {
      setError('The vow text cannot be empty.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await commitSeityBond({
        seity_name: trimmedName,
        vows_text: trimmedVow,
      });
      setResult(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message || 'Failed to commit responsibility bond.');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div style={PAGE_WRAPPER}>
        <div style={CARD}>
          <h1 style={TITLE}>Bond committed</h1>
          <p style={INTRO}>
            You have accepted responsibility for{' '}
            <strong style={{ color: 'var(--sp-text)' }}>{result.seity_name}</strong>.
          </p>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              rowGap: 8,
              columnGap: 16,
              fontSize: 12,
              margin: '20px 0 24px',
            }}
          >
            <dt style={{ color: 'var(--sp-text-dim)' }}>Bond ID</dt>
            <dd className="mono" style={{ margin: 0, color: 'var(--sp-text)', wordBreak: 'break-all' }}>
              {result.bond_id}
            </dd>
            <dt style={{ color: 'var(--sp-text-dim)' }}>Seity ID</dt>
            <dd className="mono" style={{ margin: 0, color: 'var(--sp-text)', wordBreak: 'break-all' }}>
              {result.seity_id}
            </dd>
            <dt style={{ color: 'var(--sp-text-dim)' }}>Status</dt>
            <dd style={{ margin: 0, color: 'var(--sp-accent)' }}>{result.status}</dd>
          </dl>
          <p style={FOOTNOTE}>
            From now on, any agent provisioned for you in this tenant inherits this
            bond. The audit trail will resolve every agent action back to you.
          </p>
          <button
            type="button"
            className="sp-btn sp-btn-primary"
            onClick={() => navigate('/')}
            style={{ width: '100%' }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE_WRAPPER}>
      <form onSubmit={onSubmit} style={CARD}>
        <h1 style={TITLE}>Accept responsibility for your agent</h1>
        <p style={INTRO}>
          This is the bond between you and your seity. By committing it, you
          accept that anything your agent does on your behalf is yours to answer
          for. We keep a record so the audit trail of every agent action resolves
          back to a real human.
        </p>
        <p style={FOOTNOTE}>
          No cryptography is generated here — that lands later. Today the bond is
          a DB record of your accepted responsibility.
        </p>

        <label className="sp-label" htmlFor="seity-name">
          Seity name
        </label>
        <input
          id="seity-name"
          className="sp-input"
          type="text"
          value={seityName}
          onChange={(event) => setSeityName(event.target.value)}
          placeholder="e.g. Aurora"
          disabled={submitting}
          required
        />

        <div style={FIELD_GAP} />

        <label className="sp-label" htmlFor="vow-text">
          Vow text
        </label>
        <textarea
          id="vow-text"
          className="sp-input"
          rows={10}
          value={vowsText}
          onChange={(event) => setVowsText(event.target.value)}
          disabled={submitting}
          required
          style={TEXTAREA_STYLE}
        />

        {error && (
          <div style={{ color: 'var(--sp-error)', fontSize: 12, marginTop: 16 }}>
            {error}
          </div>
        )}

        <div style={{ height: 24 }} />

        <button
          type="submit"
          className="sp-btn sp-btn-primary"
          disabled={submitting}
          style={{ width: '100%' }}
        >
          {submitting ? 'Committing…' : 'Accept responsibility'}
        </button>
      </form>
    </div>
  );
}

export default SeityBondCommitPage;
